'use client';

import { useState } from 'react';
import Link from 'next/link';

interface ContactPageClientProps {
  translations: {
    title: string;
    heroDescription: string;
    address: string;
    phone: string;
    email: string;
    map: string;
    getInTouch: string;
    getInTouchDescription: string;
    principal: string;
    schoolHours: string;
    mondayFriday: string;
    officeHours: string;
    sendMessage: string;
    sendMessageDescription: string;
    namePlaceholder: string;
    emailPlaceholder: string;
    subjectPlaceholder: string;
    messagePlaceholder: string;
    interactiveMap: string;
    mapDescription: string;
    visitOurSchool: string;
    visitOurSchoolDescription: string;
    learnMoreAboutUs: string;
    latestNews: string;
    form: {
      name: string;
      email: string;
      subject: string;
      message: string;
      send: string;
      sending: string;
      sent: string;
      error: string;
    };
  };
}

export default function ContactPageClient({ translations: t }: ContactPageClientProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('sending');

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (res.ok && data?.ok) {
        setFormStatus('sent');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (err) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: 'url(/images/back-to-school-notice.jpg)'
          }}
        />
        <div className="container-max px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
            {t.title}
          </h1>
          <p className="text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto">
            {t.heroDescription}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="container-max px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t.getInTouch}
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                {t.getInTouchDescription}
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-logo-green-light rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-logo-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t.address}
                  </h3>
                  <p className="text-logo-purple">
                    BAILE NA FINNE CO DHUN NA NGALL<br />
                    Donegal West, Ulster<br />
                    Ireland
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-logo-green-light rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-logo-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                                     <h3 className="text-lg font-semibold text-gray-900 mb-2">
                     {t.phone}
                   </h3>
                  <a 
                    href="tel:0749546209" 
                    className="text-logo-purple hover:text-logo-purple-dark transition-colors duration-200"
                  >
                    074 9546209
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-logo-green-light rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-logo-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                                     <h3 className="text-lg font-semibold text-gray-900 mb-2">
                     {t.email}
                   </h3>
                  <a 
                    href="mailto:sncolmcille@gmail.com" 
                    className="text-logo-purple hover:text-logo-purple-dark transition-colors duration-200"
                  >
                    sncolmcille@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-logo-green-light rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-logo-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t.principal}
                  </h3>
                  <p className="text-logo-purple">
                    Seaghan Mac Laifeartai
                  </p>
                </div>
              </div>
            </div>

            {/* School Hours */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t.schoolHours}
              </h3>
              <div className="space-y-2 text-gray-600">
                <div className="flex justify-between">
                  <span>{t.mondayFriday}</span>
                  <span>9:00 AM - 3:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.officeHours}</span>
                  <span>8:30 AM - 3:30 PM</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t.sendMessage}
              </h2>
              <p className="text-gray-600">
                {t.sendMessageDescription}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                                     <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                     {t.form.name} *
                   </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder={t.namePlaceholder}
                  />
                </div>

                <div>
                                     <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                     {t.form.email} *
                   </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder={t.emailPlaceholder}
                  />
                </div>
              </div>

              <div>
                                   <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                     {t.form.subject} *
                   </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                                      placeholder={t.subjectPlaceholder}
                />
              </div>

              <div>
                                   <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                     {t.form.message} *
                   </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 resize-none"
                                      placeholder={t.messagePlaceholder}
                />
              </div>

              <button
                type="submit"
                disabled={formStatus === 'sending'}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                             >
                 {formStatus === 'sending' ? t.form.sending : t.form.send}
               </button>

              {/* Form Status */}
              {formStatus === 'sent' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                     <p className="text-green-800 text-center">
                     {t.form.sent}
                   </p>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                     <p className="text-red-800 text-center">
                     {t.form.error}
                   </p>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Map Section */}
        <div className="mt-20">
                     <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
             {t.map}
           </h2>
          <div className="rounded-lg overflow-hidden w-full h-96 shadow-sm">
            <iframe
              title="Google Map - Fintown, Donegal, Ireland"
              src="https://www.google.com/maps?q=Fintown%20Donegal%20Ireland&output=embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full border-0"
              allowFullScreen
            ></iframe>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {t.visitOurSchool}
            </h3>
            <p className="text-gray-600 mb-6">
              {t.visitOurSchoolDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/about" className="btn-primary">
                {t.learnMoreAboutUs}
              </Link>
              <Link href="/notices" className="btn-secondary">
                {t.latestNews}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
